import { useEffect, useState } from "react";
import { HERO_CAROUSEL } from "@/lib/data";

/**
 * HeroCarousel — cross-fading auto-rotating background slideshow.
 * Every `intervalMs` (default 3000ms) the next image fades in over the
 * previous one with a slow Ken Burns zoom, giving a cinematic hero.
 *
 * Renders behind an overlay/content; positions itself as `absolute inset-0`.
 * The parent hero section should be `relative overflow-hidden`.
 */
export default function HeroCarousel({
    images = HERO_CAROUSEL,
    intervalMs = 3000,
    transitionMs = 1400,
}) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (!images || images.length <= 1) return;
        const id = setInterval(() => {
            setIndex((i) => (i + 1) % images.length);
        }, intervalMs);
        return () => clearInterval(id);
    }, [images, intervalMs]);

    // Preload the next image so the fade is smooth
    useEffect(() => {
        if (!images || images.length <= 1) return;
        const next = images[(index + 1) % images.length];
        const img = new Image();
        img.src = next.src;
    }, [index, images]);

    return (
        <div className="absolute inset-0 z-0" aria-hidden="true">
            {images.map((img, i) => {
                const active = i === index;
                return (
                    <div
                        key={img.src}
                        className="absolute inset-0 will-change-[opacity,transform]"
                        style={{
                            opacity: active ? 1 : 0,
                            transition: `opacity ${transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                        }}
                    >
                        <img
                            src={img.src}
                            alt={img.alt || ""}
                            loading={i === 0 ? "eager" : "lazy"}
                            className="w-full h-full object-cover"
                            style={{
                                // Ken Burns zoom while the slide is active
                                transform: active ? "scale(1.06)" : "scale(1.0)",
                                transition: `transform ${intervalMs + transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
}
